package com.klantenstop.web.components.categories

import androidx.compose.runtime.Composable
import com.klantenstop.web.components.StyledSvg
import com.klantenstop.web.components.table.DataTable
import com.klantenstop.web.components.table.TableColumnDef
import com.klantenstop.web.data.models.domain.Category
import com.materialkobweb.styles.MaterialColorVars
import com.materialkobweb.components.widgets.DsMaterialSymbols
import com.materialkobweb.components.widgets.IconButton
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.datetime.Instant
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

/**
 * Specialized category table with logo, name, date, and action columns
 *
 * @param categories List of categories to display
 * @param onNavigateToEdit Callback when edit button is clicked
 * @param onRequestDelete Callback when delete button is clicked
 */
@Composable
fun CategoryTable(
    categories: List<Category>,
    onNavigateToEdit: (String) -> Unit,
    onRequestDelete: (Category) -> Unit
) {
    DataTable(
        items = categories,
        columns = getCategoryColumns(onNavigateToEdit, onRequestDelete),
        emptyMessage = "Geen categorieën gevonden. Klik op + om er een aan te maken."
    )
}

/**
 * Column definitions for category table
 */
private fun getCategoryColumns(
    onNavigateToEdit: (String) -> Unit,
    onRequestDelete: (Category) -> Unit
): List<TableColumnDef<Category>> = listOf(
    // Logo + Name column
    TableColumnDef(
        heading = "Naam",
        resizable = true,
        comparator = compareBy { it.name },
        cellContent = { category ->
            CategoryLogoAndName(category)
        }
    ),
    // Created At column
    TableColumnDef(
        heading = "Aangemaakt",
        resizable = false,
        wrapContent = true,
        comparator = compareBy { it.createdAt },
        cellContent = { category ->
            Text(formatDate(category.createdAt))
        }
    ),
    // Actions column
    TableColumnDef(
        heading = "",
        resizable = false,
        wrapContent = true,
        comparator = null,
        cellContent = { category ->
            CategoryActions(
                onEdit = { onNavigateToEdit(category.id) },
                onDelete = { onRequestDelete(category) }
            )
        }
    )
)

@Composable
private fun CategoryLogoAndName(category: Category) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.px)
    ) {
        // Logo
        Box(
            modifier = Modifier
                .size(40.px)
                .borderRadius(8.px)
                .backgroundColor(MaterialColorVars.SurfaceContainerHighest.value()),
            contentAlignment = Alignment.Center
        ) {
            category.logo?.let { logo ->
                StyledSvg(
                    tint = MaterialColorVars.OnSurface.value(),
                    url = logo.url,
                    modifier = Modifier.size(20.px)
                )
            } ?: DsMaterialSymbols(
                icon = "category",
                modifier = Modifier.fontSize(20.px).color(MaterialColorVars.Outline.value())
            )
        }

        // Name
        SpanText(
            text = category.name,
            modifier = Modifier.fontSize(14.px).color(MaterialColorVars.OnSurface.value())
        )
    }
}

@Composable
private fun CategoryActions(
    onEdit: () -> Unit,
    onDelete: () -> Unit
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(4.px, Alignment.End)
    ) {
        IconButton(
            size = ButtonSize.SM,
            type = ButtonType.Button,
            onClick = { onEdit() }
        ) {
            DsMaterialSymbols(icon = "edit", modifier = Modifier.fontSize(18.px))
        }
        IconButton(
            size = ButtonSize.SM,
            type = ButtonType.Button,
            onClick = { onDelete() }
        ) {
            DsMaterialSymbols(
                icon = "delete",
                modifier = Modifier.fontSize(18.px).color(MaterialColorVars.Error.value())
            )
        }
    }
}

/**
 * Format Instant to date string
 */
private fun formatDate(instant: Instant): String {
    return instant.toString().substringBefore("T")
}




